import { AttributesPriorityD, PlpProduct, PlpColor } from '@/types/plp'
import { AlgoliaTracking } from '@/scripts/libs/algolia/types/analytics'
import { PRIVATE, SEMIPRIVATE } from '@/scripts/constants/pdp'
import { Colors, ProductTypeE, WcsPlpProduct } from '@/scripts/libs/wcs/types/plp'
import { PlpAttachmentsNameE, StatusMessageTypeE } from '@/scripts/libs/algolia/types/plp'
import { WcsStoreId } from '@/scripts/libs/wcs/types/store-id'
import wcs from '@/scripts/libs/wcs'

class EditorialProduct {
  public normalizeProduct(product: WcsPlpProduct): PlpProduct {
    return {
      partNumber: product.colors[0].partNumber,
      catEntryId: product.colors[0].catentryId,
      category: product.colors[0].productType,
      upc: product.colors[0].partNumber,
      colors: this.normalizeColor(product),
      sku: product.colors[0].modelCode,
    }
  }

  private normalizeColor(product: WcsPlpProduct): Array<PlpColor> {
    const commonAttr = {
      algoliaTracking: {} as AlgoliaTracking,
      customizable: product.remixable?.url ?? '',
      name: product.modelName,
    }
    const colors = (Array.isArray(product.colors) && product.colors) || []
    const normalizedColors = colors.map((item) => {
      return Object.assign({}, commonAttr, {
        attributesPriority: this.mapAttributes(item),
        catEntryId: item.catentryId,
        electronics: item.productType === ProductTypeE.ELECTRONICS,
        images: {
          primary: item.productImage,
          secondary: this.removeImpolicy(item.productImageHover),
        },
        moco: item.moco,
        onSale: item.onSale === PRIVATE || item.onSale === SEMIPRIVATE,
        partNumber: item.partNumber,
        polarized: item.isPolarized,
        preorder: item.isPreorder,
        hasWornImage: item.productImageHoverView === PlpAttachmentsNameE.ON_MODEL,
        price: item.offerPrice,
        product_type: item.productType,
        promotions: item.priceArray || [],
        statusMessage: this.mapStatusMessage(item),
        sustainable: item.isCollectionSustainable,
        url: item.pdpUrl,
      })
    })

    return normalizedColors
  }

  private removeImpolicy(url: string): string {
    if (url.includes('impolicy')) {
      return url.split('?')[0]
    }
    return url
  }

  private mapAttributes(product: Colors): Array<AttributesPriorityD> {
    const priority = [
      {
        type: 'sustainable',
        value: product.isCollectionSustainable,
        icon: '',
        label: 'common.product_tile.attributes.alternative_materials',
      },
      {
        type: 'polarized',
        value: product.isPolarized,
        icon: 'sunglasses-polarized-black',
        label: 'common.product_tile.attributes.polarized',
      },
      {
        type: 'transitions',
        value: product.modelName.includes('TRANSITIONS'),
        icon: 'transitions-lens',
        label: 'common.product_tile.attributes.transitions',
      },
    ]
    return priority.filter((el: AttributesPriorityD) => el.value)
  }

  private mapStatusMessage = (
    product: Colors
  ): { label: string; type: StatusMessageTypeE } | undefined => {
    if (product.isPreorder) {
      return {
        label: 'common.product_tile.status_message.pre_order',
        type: StatusMessageTypeE.PREORDER,
      }
    }

    if (product.isPreRelease) {
      return {
        label: 'common.product_tile.status_message.pre_release',
        type: StatusMessageTypeE.PRERELEASE,
      }
    }

    if (product.isLimitedEdition) {
      return {
        label: 'common.product_tile.status_message.limited_edition',
        type: StatusMessageTypeE.LIMITED,
      }
    }

    if (product.webExclusive) {
      const { storeId } = wcs.getStoreConfig()
      const isJapan = storeId === WcsStoreId.JAPAN
      return isJapan
        ? { label: 'オンライン限定', type: StatusMessageTypeE.WEBEXCLUSIVEJP }
        : {
            label: 'common.product_tile.status_message.web_exclusive',
            type: StatusMessageTypeE.WEBEXCLUSIVE,
          }
    }

    if (product.isNew) {
      return {
        label: 'common.product_tile.status_message.is_new',
        type: StatusMessageTypeE.NEW,
      }
    }

    return undefined
  }
}

export default new EditorialProduct()
